import isEmpty from "lodash/isEmpty";
import find from "lodash/find";
import map from "lodash/map";
import { createSelector } from "reselect";
import { validateIccIdSelector } from "./validateIccId";
import get from "lodash/get";
import cloneDeep from "lodash/cloneDeep"
import orderBy from "lodash/orderBy";
import { PLAN_TYPE, DEFAULT_PREPAID_VALUES } from "utils/constants";

import {
  preCheckoutSelector,
  globalPlanConfigurationSelector,
  currencyFormatSelector,
  bannerSelector,
  localeSelector
} from "ducks/selectors";

import {
  formatCurrency,
  lower,
  getPlanBenefits,
  calTotalData
} from "utils/helpers";
import {
  CURRENCY_STYLES,
  formatCurrency as formatCurrencyWithI18n
} from "utils/i18nFormatter";

export const eligiblePlansDomain = state =>
  get(state, "api.eligiblePlans.data.productVariants", []);

export const homeBannersDomain = state =>
  get(state, "api.homeBanners.data", []);

// ***************************************************************
// ******************** eligiblePlans selectors ********************
// ***************************************************************

const formatDiscountValue = (plan, currencyFormat) => {
  const discountPrice = get(plan, "discountedPrice");
  const price = get(plan, "price");
  const finalPrice = discountPrice >= price ? price : discountPrice;
  return formatCurrency(finalPrice, currencyFormat, false);
};
const getDiscountValue = plan => {
  const discountPrice = get(plan, "discountedPrice");
  const price = get(plan, "price");
  const finalPrice = discountPrice >= price ? price : discountPrice;
  return finalPrice;
};

const formatCurrencyValue = (plan, currencyFormat) => {
  const discountPrice = get(plan, "discountedPrice");
  const price = get(plan, "price");
  const finalPrice = discountPrice >= price ? null : price;
  return formatCurrency(finalPrice, currencyFormat, false);
};

const getCurrencyValue = plan => {
  const discountPrice = get(plan, "discountedPrice");
  const price = get(plan, "price");
  const finalPrice = discountPrice >= price ? null : price;
  return finalPrice;
};

const formatRecurrence = plan => {
  const productCategory = get(plan, "productCategory");
  if (productCategory && productCategory === "prepaid") {
    return "pack";
  } else {
    let recurrence = get(plan, "attributes", []).find(
      attribute => attribute.name === "recurrent"
    )?.value;
    return recurrence !== "false" ? "mo" : "pack";
  }
};

const modifyAddons = (eligibleAddons, currencyFormat) => {
  const eligibleItems = eligibleAddons.map(eligibleAddon => {
    const { benefits } = eligibleAddon;
    const displayBenefit = benefits;
    // .filter(item =>
    //   includes(ADDON_BENEFIT_TYPES, item?.selectedType)
    // );
    // const displayPrice=get(eligibleAddon, "discountedPrice")>
    return {
      ...eligibleAddon,
      benefits: displayBenefit,
      banners: get(eligibleAddon, "urls.bannerImageUrl"),
      recurrence: formatRecurrence(eligibleAddon),
      displayPrice: formatCurrencyWithI18n(
        get(eligibleAddon, "discountedPrice") ||
        getCurrencyValue(eligibleAddon),
        {
          style: CURRENCY_STYLES.SHORT
        }
      ),
      displayPrice_old: formatCurrency(
        Math.round(
          get(eligibleAddon, "discountedPrice") ||
          formatCurrencyValue(eligibleAddon, currencyFormat)
        ),
        currencyFormat,
        false
      ),
      oldPrice: get(eligibleAddon, "discountedPrice")
        ? getCurrencyValue(eligibleAddon)
          ? formatCurrencyWithI18n(getCurrencyValue(eligibleAddon), {
            style: CURRENCY_STYLES.SHORT
          })
          : null
        : null,
      oldPrice_old: get(eligibleAddon, "discountedPrice")
        ? formatCurrencyValue(eligibleAddon, currencyFormat)
        : null
    };
  });
  return eligibleItems;
};

export const eligiblePlansSelector = createSelector(
  eligiblePlansDomain,
  validateIccIdSelector,
  currencyFormatSelector,
  preCheckoutSelector,
  localeSelector,
  (eligiblePlans, iccIdDetails, currencyFormat, formValues, locale) => {
    const plansList = orderBy(eligiblePlans, ["price"], ["asc"]);
    let planType = lower(get(formValues, "planType"));
    const planListWithData = plansList.map(plan => ({
      ...plan,
      totalData: calTotalData(plan, locale),
      validityDays: find(plan?.attributes, { name: "validity" })
    }));

    if (planType === PLAN_TYPE["PREPAID"]) {
      eligiblePlans = [
        {
          sku: DEFAULT_PREPAID_VALUES.sku,
          baseProductVariantSku: DEFAULT_PREPAID_VALUES.sku,
          productCategory: PLAN_TYPE["PREPAID"],
          title: DEFAULT_PREPAID_VALUES.title,
          attributes: [
            {
              name: "price",
              value: DEFAULT_PREPAID_VALUES.price
            },
            {
              name: "recurrent",
              value: DEFAULT_PREPAID_VALUES.recurrent
            }
          ],
          associatedProductVariants: planListWithData
        }
      ];
    }

    eligiblePlans = eligiblePlans?.map(eligiblePlan => ({
      ...eligiblePlan,
      benefits: getPlanBenefits(eligiblePlan),
      displayPrice: formatCurrencyWithI18n(getDiscountValue(eligiblePlan), {
        style: CURRENCY_STYLES.SHORT
      }),
      displayPrice_old: formatDiscountValue(eligiblePlan, currencyFormat),
      oldPrice: getCurrencyValue(eligiblePlan)
        ? formatCurrencyWithI18n(getCurrencyValue(eligiblePlan), {
          style: CURRENCY_STYLES.SHORT
        })
        : null,
      oldPrice_old: formatCurrencyValue(eligiblePlan, currencyFormat),
      value: get(eligiblePlan, "discountedPrice") || get(eligiblePlan, "price"),
      recurrence: formatRecurrence(eligiblePlan),
      associatedProductVariants: modifyAddons(
        eligiblePlan?.associatedProductVariants ?? [],
        currencyFormat
      )
    }));

    const iccIdPlanName = get(iccIdDetails, "plan.name");
    const iccIdPlan = find(eligiblePlans, { name: iccIdPlanName });
    // treat only sim plan as eligible it is valid
    if (!isEmpty(iccIdPlan)) return [iccIdPlan];

    return eligiblePlans;
  }
);

export const eligiblePlansGropuSelector = createSelector(
  eligiblePlansSelector,
  eligiblePlans => {
    const output = [];
    let eligiblePlansGroup = cloneDeep(eligiblePlans)

    // Dictionary to group products by their group labels
    if (eligiblePlansGroup.length === 0) return

    let productVariants = eligiblePlansGroup[0].associatedProductVariants
     
   
    // productVariants = orderBy(
    //   eligiblePlansGroup[0].associatedProductVariants,
    //   [item => parseInt(item.validityDays.value)],
    //   ["desc"]
    // );

    // Iterate through each product variant
    productVariants.forEach((product) => {
      let groupLabel = null;
      let isFound = false
      // Check for labels starting with "Group"
      product.labels.forEach(label => {
        if (label.startsWith("Group")) {
          groupLabel = label;
          isFound = true
        }
      });

      if (!isFound) {
        output.push({
          productType: "REGULAR",
          groupLabel: "",
          product: [{
            variant: product
          }]
        });
      } else {

      const isAvailable = output.find(label => label.groupLabel === groupLabel)

      if (!isAvailable) {
          const groupedProducts =
            productVariants.filter(product => product.labels.includes(groupLabel))
              .map((product, idx) => ({
                variant: product,
                isSelected: idx === 0
              }))

          output.push({
            productType: "GROUP",
            groupLabel,
            product: groupedProducts
          });
        }
      }
    })

    eligiblePlansGroup[0].associatedProductVariants = output
    return eligiblePlansGroup
  }
)



// ***************************************************************
// ******************* eligiblePlans makeSelect ********************
// ***************************************************************

export const homeBannerSelector = createSelector(
  bannerSelector,
  homeBanners => {
    return map(homeBanners, image => {
      return { url: image?.image, link: image?.webUrl, title: image?.title };
    });
  }
);

export const fetchPlanTypeSelector = createSelector(
  preCheckoutSelector,
  globalPlanConfigurationSelector,
  formValues => {
    return lower(get(formValues, "planType"));
  }
);

export const makeSelectEligiblePlans = () => eligiblePlansSelector;

export const makeSelectHomeBanner = () => homeBannerSelector;

export const makeSelectPlanTypeSelector = () => fetchPlanTypeSelector;

export const makeSelectEligiblePlansGroup = () => eligiblePlansGropuSelector;
